/** The headline, the search description, the byline and a text area that grows with its text (kept apart
 *  from the TipTap editor so the page can load that lazily). */
import { useLayoutEffect, useRef, type TextareaHTMLAttributes } from 'react'
import { focus } from './ui'

/** The headline (styled as it prints), the search description (meta_description: Google and link previews,
 *  never on the page, so it is labelled as such rather than looking like a standfirst) and the byline. A
 *  subtitle belongs in the text as its opening <h3>; when there is one, the byline box says the byline prints
 *  under it, as in print (the editor shows the byline box above the text either way). */
export function Headline({ title, deck, byline, subtitle = null, editable, onTitle, onDeck, onByline }: {
  title: string
  byline: string
  deck: string
  subtitle?: string | null
  editable: boolean
  onTitle: (v: string) => void
  onDeck: (v: string) => void
  onByline: (v: string) => void
}) {
  return (
    <div className="mb-[30px] flex flex-col gap-3.5">
      <GrowingTextarea
        aria-label="Headline" value={title} readOnly={!editable} rows={1} placeholder="Headline"
        onChange={(e) => onTitle(e.target.value.replace(/\s*\n\s*/g, ' '))}
        className={`m-0 w-full resize-none overflow-hidden border-0 bg-transparent p-0 font-serif text-[clamp(30px,4vw,44px)] font-medium leading-[1.08] tracking-[-0.015em] text-paper placeholder:text-paper-faint ${focus}`}
      />
      {(editable || deck) && (
        <label className="flex flex-col gap-1">
          <span className="font-sans text-[12px] font-semibold uppercase tracking-[0.08em] text-paper-faint">
            Search description · not shown on the page
          </span>
          <GrowingTextarea
            aria-label="Search description" value={deck} readOnly={!editable} rows={1}
            placeholder="For Google and link previews. A subtitle goes at the top of the text."
            onChange={(e) => onDeck(e.target.value.replace(/\s*\n\s*/g, ' '))}
            className={`m-0 w-full resize-none overflow-hidden border-0 bg-transparent p-0 font-sans text-[15px] leading-[1.45] text-paper-dim placeholder:text-paper-faint ${focus}`}
          />
        </label>
      )}
      {(editable || byline) && (
        <div className="flex flex-col gap-1">
          <GrowingTextarea
            aria-label="Byline" value={byline} readOnly={!editable} rows={1} maxLength={200} placeholder="By …"
            onChange={(e) => onByline(e.target.value.replace(/\s*\n\s*/g, ' '))}
            className={`m-0 w-full resize-none overflow-hidden border-0 bg-transparent p-0 font-sans text-[14px] uppercase tracking-[0.08em] text-paper-dim placeholder:text-paper-faint ${focus}`}
          />
          {subtitle && (
            <span className="font-sans text-[12px] text-paper-faint">
              Printed under the subtitle “{subtitle}”, as in print.
            </span>
          )}
        </div>
      )}
    </div>
  )
}

/** A textarea as tall as its text, so a headline wraps like print rather than scrolling. */
export function GrowingTextarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const el = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    const t = el.current
    if (!t) return
    t.style.height = 'auto'
    t.style.height = `${t.scrollHeight}px`
  }, [props.value])
  return <textarea ref={el} {...props} />
}
